import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Button,
  useTheme,
  Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { PWAInstallButton } from '../common/PWAInstallButton';
import { NavTab } from './Sidebar';
import type { RootState } from '../../app/store';
import { useLogoutMutation } from '../../lib/api/authApi';
import { ROUTES } from '../../config/routes';
import { useTranslation } from 'react-i18next';
import { i18n, LANGUAGES, persistLanguage } from '../../lib/i18n';
import type { AppLanguage } from '../../lib/i18n';

interface HeaderProps {
  currentTab: NavTab;
  onSelectTab: (tab: NavTab) => void;
  unreadNotifications: number;
  unreadMessages: number;
  onOpenNotifications: () => void;
  onOpenMessages: () => void;
  onOpenMobileMenu?: () => void;
  /** Les profils/paramètres pointent vers /pro/* ou /app/* selon le rôle session. */
  isAdmin?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  currentTab,
  onSelectTab,
  unreadNotifications,
  unreadMessages,
  onOpenNotifications,
  onOpenMessages,
  onOpenMobileMenu,
  isAdmin = false,
}) => {
  const navigate = useNavigate();
  const sessionUser = useSelector((s: RootState) => s.session.user);
  const isClientSession = sessionUser?.role === 'client';
  const [logout] = useLogoutMutation();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const currentLang = (i18n.language as AppLanguage) === 'en' ? 'en' : 'fr';

  const changeLanguage = (lng: AppLanguage) => {
    void i18n.changeLanguage(lng);
    persistLanguage(lng);
    setLangAnchorEl(null);
  };

  return (
    <Box
      sx={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 2, md: 3 },
        py: 1,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        transition: 'background-color 0.2s, border-color 0.2s',
      }}
    >
      {/* Left empty spacer or minimal container */}
      <Box sx={{ display: 'flex', alignItems: 'center' }} />

      {/* Right Tools Area */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 } }}>
        <PWAInstallButton />

        {/* Language selector without emoji */}
        <Button
          size="small"
          onClick={(e) => setLangAnchorEl(e.currentTarget)}
          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: '0.82rem',
            minWidth: 58,
            px: 1,
            py: 0.4,
            borderRadius: '8px',
            bgcolor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {currentLang.toUpperCase()}
        </Button>
        <Menu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={() => setLangAnchorEl(null)}
          slotProps={{ paper: { sx: { borderRadius: '10px', mt: 0.5, minWidth: 140, border: '1px solid', borderColor: 'divider' } } }}
        >
          {LANGUAGES.map((lang) => (
            <MenuItem
              key={lang.code}
              selected={currentLang === lang.code}
              onClick={() => changeLanguage(lang.code)}
              sx={{ fontSize: '0.85rem', fontWeight: 600 }}
            >
              {t(`header.language.${lang.code}`)}
            </MenuItem>
          ))}
        </Menu>

        {/* Chat / Messages Button */}
        <IconButton
          onClick={onOpenMessages}
          size="small"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            color: 'text.secondary',
            bgcolor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { color: '#FF3567' },
          }}
        >
          <Badge
            color="primary"
            variant="dot"
            invisible={unreadMessages === 0}
            sx={{ '& .MuiBadge-badge': { bgcolor: '#FF3567', border: `2px solid ${isLight ? '#FFFFFF' : '#141417'}` } }}
          >
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />
          </Badge>
        </IconButton>

        {/* Notifications Button */}
        <IconButton
          onClick={onOpenNotifications}
          size="small"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            color: 'text.secondary',
            bgcolor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { color: '#FF3567' },
          }}
        >
          <Badge
            color="primary"
            variant="dot"
            invisible={unreadNotifications === 0}
            sx={{ '& .MuiBadge-badge': { bgcolor: '#FF3567', border: `2px solid ${isLight ? '#FFFFFF' : '#141417'}` } }}
          >
            <NotificationsNoneRoundedIcon sx={{ fontSize: 19 }} />
          </Badge>
        </IconButton>

        {/* User Profile Avatar with signature #FF3567 ring */}
        <Box
          onClick={(e) => setUserAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            pl: 0.5,
          }}
        >
          <Avatar
            alt={sessionUser?.name ?? t('guest.name')}
            src={undefined}
            sx={{
              width: 34,
              height: 34,
              border: '2px solid #FF3567',
              transition: 'opacity 0.15s ease',
              '&:hover': { opacity: 0.85 },
              bgcolor: '#FF3567',
              fontSize: '0.85rem',
              fontWeight: 800,
            }}
          >
            {(sessionUser?.name ?? '?').charAt(0).toUpperCase()}
          </Avatar>
        </Box>

        {/* User profile menu */}
        <Menu
          anchorEl={userAnchorEl}
          open={Boolean(userAnchorEl)}
          onClose={() => setUserAnchorEl(null)}
          slotProps={{ paper: { sx: { borderRadius: '12px', mt: 1, minWidth: 220, p: 0.5, border: '1px solid', borderColor: 'divider' } } }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
              {sessionUser?.name ?? t('guest.name')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {sessionUser?.email ?? ''}
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          {sessionUser ? (
            <>
              <MenuItem
                selected={currentTab === 'profile'}
                onClick={() => {
                  navigate(isClientSession ? ROUTES.app.profile : ROUTES.pro.profile);
                  setUserAnchorEl(null);
                }}
                sx={{ fontSize: '0.84rem', fontWeight: 600 }}
              >
                {t('header.profile')}
              </MenuItem>
              <MenuItem
                selected={currentTab === 'settings'}
                onClick={() => {
                  navigate(isClientSession ? ROUTES.app.settings : ROUTES.pro.settings);
                  setUserAnchorEl(null);
                }}
                sx={{ fontSize: '0.84rem' }}
              >
                {t('header.settings')}
              </MenuItem>
              {isAdmin && (
                <MenuItem
                  onClick={() => {
                    navigate(ROUTES.admin.root);
                    setUserAnchorEl(null);
                  }}
                  sx={{ fontSize: '0.84rem', fontWeight: 600 }}
                >
                  {t('header.adminConsole')}
                </MenuItem>
              )}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setUserAnchorEl(null);
                  void logout();
                  navigate(ROUTES.login);
                }}
                sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#EF4444' }}
              >
                <LogoutRoundedIcon sx={{ fontSize: 17, mr: 1 }} />
                {t('header.logout')}
              </MenuItem>
            </>
          ) : (
            <MenuItem
              onClick={() => {
                setUserAnchorEl(null);
                navigate(ROUTES.login);
              }}
              sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#3B82F6' }}
            >
              {t('header.login')}
            </MenuItem>
          )}
        </Menu>

        {/* Mobile menu trigger */}
        {onOpenMobileMenu && (
          <IconButton
            onClick={onOpenMobileMenu}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              width: 36,
              height: 36,
              color: 'text.secondary',
            }}
          >
            <MenuRoundedIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
